export const MAINTENANCE_BLOCK_IDS = ["block_a", "block_s_car", "block_s_motorcycle", "block_k"];

export const DEFAULT_MAINTENANCE_API_URL =
  "https://parking.haiotsys.com/api/public/parking-maintenance";

// Reject partial or malformed snapshots: a bad response must never clear a flag.
export function parseMaintenanceSnapshot(payload) {
  if (
    !payload ||
    !Number.isSafeInteger(payload.revision) ||
    payload.revision < 0 ||
    !(payload.updated_at === null || typeof payload.updated_at === "string") ||
    !Array.isArray(payload.items) ||
    payload.items.length !== MAINTENANCE_BLOCK_IDS.length
  ) {
    throw new Error("Invalid maintenance snapshot");
  }

  const flags = {};
  for (const item of payload.items) {
    if (
      !item ||
      !MAINTENANCE_BLOCK_IDS.includes(item.id) ||
      typeof item.maintenance !== "boolean" ||
      Object.prototype.hasOwnProperty.call(flags, item.id)
    ) {
      throw new Error("Invalid maintenance block");
    }
    flags[item.id] = item.maintenance;
  }
  return { revision: payload.revision, updatedAt: payload.updated_at, flags };
}

export function maintenanceDisplay(flags, blockId, available) {
  if (flags[blockId] === true) return "維修中";
  if (flags[blockId] === false) return available;
  return "—";
}

export function maintenanceCacheKey(url) {
  return `parking-maintenance:v1:${encodeURIComponent(url)}`;
}

export function readCachedMaintenance(storage, url) {
  try {
    const cached = JSON.parse(storage.getItem(maintenanceCacheKey(url)));
    if (!cached || cached.version !== 1 || cached.url !== url) return null;
    return parseMaintenanceSnapshot(cached.payload);
  } catch (error) {
    return null;
  }
}

// request() returns { promise, cancel }. Keeping transport outside this helper
// lets both the screen and tests enforce the same polling/lifecycle behaviour.
export function startMaintenanceSync({
  url,
  request,
  onSnapshot,
  onStatus,
  storage,
  intervalMs = 5000,
  schedule = setInterval,
  unschedule = clearInterval,
}) {
  let running = true;
  let inFlight = null;
  onStatus("pending");
  const cached = readCachedMaintenance(storage, url);
  if (cached) onSnapshot(cached);

  async function refresh() {
    if (!running || inFlight) return;
    const active = { cancel: null };
    inFlight = active;
    try {
      const operation = request(url);
      active.cancel = operation.cancel;
      const payload = await operation.promise;
      if (!running || inFlight !== active) return;
      const snapshot = parseMaintenanceSnapshot(payload);
      onSnapshot(snapshot);
      onStatus("synced");
      try {
        storage.setItem(
          maintenanceCacheKey(url),
          JSON.stringify({ version: 1, url, payload }),
        );
      } catch (error) {
        // A disabled/full local cache must not prevent a successful sync.
      }
    } catch (error) {
      if (running && inFlight === active) onStatus("error");
    } finally {
      if (inFlight === active) inFlight = null;
    }
  }

  const timer = schedule(refresh, intervalMs);
  refresh();

  return {
    refresh,
    stop() {
      if (!running) return;
      running = false;
      unschedule(timer);
      if (inFlight && typeof inFlight.cancel === "function") {
        inFlight.cancel();
      }
      inFlight = null;
    },
  };
}
